/**
 * @file ColumnFilterNote.js
 * @version 2.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-26
 * @modified 2026-10-14
 * @description "Supplier · clear" — the toolbar pill naming what is narrowing
 *              the results.
 *
 *              A header filter is invisible from anywhere except the header it
 *              was set on, and a result count that is smaller than expected for
 *              an unremembered reason is how people stop trusting a screen.
 *              These filters persist across searches and pagination, which
 *              makes that far easier to do — hence naming the columns here
 *              rather than only counting them, and a one-click clear that does
 *              not require remembering which headers are on.
 *
 *              Since 2.1.0 it also counts the screen's HEADER filters (search
 *              text, dropdowns, dates), passed as `header` from
 *              utils/activeFilters.activeHeaderFilters. `onClear` is the
 *              screen's own Clear Filters handler, so "clear" here does exactly
 *              what the header button does. A screen passing neither behaves as
 *              before: column filters only, cleared by the hook.
 *
 *              2.2.0 (2026-10-14): a section's filter (the Status header's
 *              Billed, Invoiced, …) is named by its section label, not its key.
 */

import React from 'react';

/** The label of the section whose filter key is `key`, if any. */
function sectionLabel(definitions, key) {
  for (const def of Object.values(definitions || {})) {
    const section = (def?.sections || []).find((sec) => sec.key === key);
    if (section) return section.label;
  }
  return null;
}

function ColumnFilterNote({ cols, header = null, onClear = null }) {
  const columnNames = cols
    ? Object.keys(cols.selected)
      .filter((k) => Array.isArray(cols.selected[k]))
      .map((k) => cols.definitions[k]?.label || sectionLabel(cols.definitions, k) || k)
    : [];
  const names = [...(header?.labels || []), ...columnNames];
  if (!names.length) return null;
  const clear = onClear || cols?.clearEverything;
  return (
    <span className="col-filter-note" title={`Filtering on ${names.join(', ')}`}>
      <span className="material-symbols-rounded" style={{ fontSize: '14px' }}>filter_alt</span>
      <span className="cfn-label">
        {names.length === 1 ? names[0] : `${names.length} filters`}
      </span>
      <button type="button" onClick={clear}>clear</button>
    </span>
  );
}

export default ColumnFilterNote;
